/**
 * What the portal says about a password before it is typed, and what it says when the server refuses.
 *
 * Two screens set a password: account settings, and the one behind an invitation or a recovery link.
 * They agree because the rule lives here rather than in each of them.
 *
 * **The server is the authority and this file is not a second copy of the policy.** The rules live
 * in the Supabase project, `supabase/config.toml` mirrors them for the local stack so the two refuse
 * the same passwords, and GoTrue's own answer is what produces every message below. What is
 * duplicated is the *hint*, and only the hint, because the alternative is a person on an invitation
 * screen typing twelve lowercase letters and learning about capitals from a rejection. If the
 * project ever relaxes its rules the hint is merely stricter than necessary, which is the safe
 * direction for a sentence to drift in.
 */

/** The portal's own floor, enforced in the form so a submit is not spent to learn it. */
export const MIN_PASSWORD_LENGTH = 12;

export const PASSWORD_HINT =
  "Minst 12 tecken, med både stora och små bokstäver, minst en siffra och minst ett specialtecken.";

/**
 * GoTrue's three reasons, in Swedish.
 *
 * `pwned` is the leaked-password check, which Supabase gates behind a paid plan and which is
 * therefore off on this project. The sentence exists anyway: the day the plan changes, the reason
 * starts arriving and a screen with no wording for it would show the fallback instead.
 */
const REASON_TEXT: Record<string, string> = {
  length: `Lösenordet är för kort. Det behöver minst ${MIN_PASSWORD_LENGTH} tecken.`,
  characters:
    "Lösenordet behöver både stora och små bokstäver, minst en siffra och minst ett specialtecken.",
  pwned: "Lösenordet finns med i kända läckor. Välj ett annat."
};

const FALLBACK = "Lösenordet uppfyller inte kraven. Välj ett annat.";

interface MaybeWeakPassword {
  code?: string;
  reasons?: unknown;
}

/**
 * The sentence for a refusal, or null when the refusal was not about the password's strength.
 *
 * The reasons are read from the error rather than guessed from the password, because the project's
 * rules are the project's and this code cannot see them. An unrecognised reason falls back rather
 * than being rendered raw: GoTrue's own message names the character classes in English, inside a
 * string written for a developer.
 */
export function weakPasswordMessage(error: unknown): string | null {
  const candidate = error as MaybeWeakPassword | null;

  if (candidate?.code !== "weak_password") {
    return null;
  }

  const reasons = Array.isArray(candidate.reasons) ? candidate.reasons : [];
  const said = reasons
    .map((reason) => (typeof reason === "string" ? REASON_TEXT[reason] : undefined))
    .filter((text): text is string => Boolean(text));

  return said.length > 0 ? said.join(" ") : FALLBACK;
}
